import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Plus, Copy, Link2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from 'sonner';
import { generateInviteLink } from '@/utils/linkUtils';
import { Sentry } from '@/lib/sentry';

interface Organization {
  id: string;
  name: string;
  username?: string;
  type: string;
}

interface SelectedCarrier extends Organization {
  isNew?: boolean;
  contact?: string;
}

interface InviteCarrierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInviteSent?: () => void;
  connections?: any[];
  invitations?: any[];
}

export const InviteCarrierModal: React.FC<InviteCarrierModalProps> = ({
  isOpen,
  onClose,
  onInviteSent,
  connections = [],
  invitations = []
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  
  const [carrierName, setCarrierName] = useState("");
  const [searchResults, setSearchResults] = useState<Organization[]>([]);
  const [selectedCarriers, setSelectedCarriers] = useState<SelectedCarrier[]>([]);
  const [generatedLinks, setGeneratedLinks] = useState<string[]>([]);
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  const detectInputType = (input: string): 'email' | 'phone' | 'text' => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\+]?[1-9][\d]{3,14}$/;
    
    if (emailRegex.test(input)) return 'email';
    if (phoneRegex.test(input.replace(/[\s\-\(\)]/g, ''))) return 'phone';
    return 'text';
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const searchCarriers = async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, username, type')
        .or(`name.ilike.%${searchTerm}%, username.ilike.%${searchTerm}%`)
        .neq('type', 'customer')
        .neq('id', currentOrganization?.organization_id)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching carriers:', error);
      setSearchResults([]);
    }
  };

  const addCarrier = (carrier: Organization | SelectedCarrier) => {
    const isDuplicate = selectedCarriers.some(c => 
      c.id === carrier.id || 
      (c.isNew && 'isNew' in carrier && carrier.isNew && c.contact === carrier.contact)
    );
    if (isDuplicate) {
      toast.error(t('carrierManagement.alreadyAdded'));
      return;
    }

    setSelectedCarriers(prev => [...prev, carrier as SelectedCarrier]);
    setCarrierName("");
    setSearchResults([]);
  };

  const removeCarrier = (carrierId: string) => {
    setSelectedCarriers(prev => prev.filter(c => c.id !== carrierId));
  };

  const isAlreadyConnected = (carrierId: string): boolean => {
    return connections.some(conn => 
      (conn.inviter_org_id === carrierId && conn.invitee_org_id === currentOrganization?.organization_id) ||
      (conn.invitee_org_id === carrierId && conn.inviter_org_id === currentOrganization?.organization_id)
    );
  };

  const hasPendingInvitation = (carrierId: string): boolean => {
    return invitations.some(inv => 
      inv.invited_organization_id === carrierId || inv.organization_id === carrierId
    );
  };

  const handleInviteCarriers = async () => {
    if (!user || !currentOrganization || selectedCarriers.length === 0) return;

    setSendingInvitations(true);
    try {
      for (const carrier of selectedCarriers) {
        if (carrier.isNew && carrier.contact) {
          // Check for existing active invitation for this target
          const { data: existingInvitations } = await supabase
            .from('connection_invitations')
            .select('id')
            .eq('inviter_org_id', currentOrganization.organization_id)
            .eq('invitation_target', carrier.contact)
            .eq('status', 'active');

          let invitationId: string;

          if (existingInvitations && existingInvitations.length > 0) {
            // Use existing invitation ID for resend
            invitationId = existingInvitations[0].id;
          } else {
            // Create new invitation only if none exists
            const { data: invitationData, error: inviteError } = await supabase
              .from('connection_invitations')
              .insert({
                inviter_org_id: currentOrganization.organization_id,
                invited_by: user.id,
                invitation_type: detectInputType(carrier.contact),
                invitation_target: carrier.contact,
                connection_type: 'carrier',
                status: 'active'
              })
              .select('id')
              .single();

            if (inviteError) throw inviteError;
            invitationId = invitationData.id;
          }

          // Notification will be sent automatically via database trigger if this is a new invitation
          // For existing invitations (resend), we need to create a new notification record in the queue
          if (existingInvitations && existingInvitations.length > 0) {
            await supabase
              .from('notification_queue')
              .insert({
                event_type: 'invitation_sent',
                event_data: { invitation_id: invitationId },
                channels: ['slack', 'email'],
                recipients: { email: [carrier.contact] },
                priority: 'normal',
                scheduled_for: new Date().toISOString()
              });
          }
        } else {
          // Check for existing active invitation for this organization
          const { data: existingInvitations } = await supabase
            .from('connection_invitations')
            .select('id')
            .eq('inviter_org_id', currentOrganization.organization_id)
            .eq('invitation_target', carrier.id)
            .eq('status', 'active');

          let invitationId: string;

          if (existingInvitations && existingInvitations.length > 0) {
            // Use existing invitation ID for resend
            invitationId = existingInvitations[0].id;
          } else {
            // Create invitation for existing organization (not direct connection)
            const { data: invitationData, error: inviteError } = await supabase
              .from('connection_invitations')
              .insert({
                inviter_org_id: currentOrganization.organization_id,
                invited_by: user.id,
                invitation_type: 'organization',
                invitation_target: carrier.id, // Store the organization ID
                invitee_org_id: carrier.id, // Also store in the dedicated field
                connection_type: 'carrier',
                status: 'active'
              })
              .select('id')
              .single();

            if (inviteError) throw inviteError;
            invitationId = invitationData.id;
          }

          // Notification will be sent automatically via database trigger if this is a new invitation
          // For existing invitations (resend), we need to create a new notification record in the queue
          if (existingInvitations && existingInvitations.length > 0) {
            await supabase
              .from('notification_queue')
              .insert({
                event_type: 'invitation_sent',
                event_data: { invitation_id: invitationId },
                channels: ['slack', 'email'],
                recipients: { email: [] }, // Will be determined by the notification system
                priority: 'normal',
                scheduled_for: new Date().toISOString()
              });
          }
        }
      }

      // Track successful invitation sending
      Sentry.addBreadcrumb({
        message: `Successfully sent ${selectedCarriers.length} carrier invitations`,
        category: 'business_success',
        level: 'info',
        data: { 
          carrierCount: selectedCarriers.length,
          inviterOrgId: currentOrganization.organization_id,
          invitationTypes: selectedCarriers.map(c => c.isNew ? 'new_contact' : 'existing_org')
        }
      });

      toast.success(t('carrierManagement.invitationsSent'));
      setSelectedCarriers([]);
      onInviteSent?.();
      onClose();
    } catch (error) {
      console.error('Error sending invitations:', error);
      
      // Capture critical invitation error
      Sentry.captureException(error, {
        tags: {
          operation: 'send_carrier_invitations',
          critical: true,
          business_process: 'carrier_invitation'
        },
        extra: {
          carrierCount: selectedCarriers.length,
          selectedCarriers: selectedCarriers.map(c => ({
            id: c.id,
            name: c.name,
            isNew: c.isNew,
            type: c.type
          })),
          inviterOrgId: currentOrganization?.organization_id,
          userId: user?.id
        }
      });
      
      toast.error(t('carrierManagement.invitationError'));
    } finally {
      setSendingInvitations(false);
    }
  };

  // Check for existing active link when modal opens
  useEffect(() => {
    if (isOpen && user && currentOrganization) {
      const checkExistingLink = async () => {
        try {
          const { data: existingLinks } = await supabase
            .from('connection_invitations')
            .select('id, invitation_target')
            .eq('inviter_org_id', currentOrganization.organization_id)
            .eq('invitation_type', 'link')
            .eq('status', 'active')
            .limit(1);

          if (existingLinks && existingLinks.length > 0) {
            const existingInviteLink = generateInviteLink(existingLinks[0].id);
            setGeneratedLinks([existingInviteLink]);
          }
        } catch (error) {
          console.error('Error checking existing links:', error);
        }
      };
      
      checkExistingLink();
    }
  }, [isOpen, user, currentOrganization]);

  const handleShareGenericLink = async () => {
    if (!user || !currentOrganization) return;

    setGeneratingLink(true);
    try {
      // First check if there's already an active link invitation
      const { data: existingLinks } = await supabase
        .from('connection_invitations')
        .select('id, invitation_target')
        .eq('inviter_org_id', currentOrganization.organization_id)
        .eq('invitation_type', 'link')
        .eq('status', 'active')
        .limit(1);

      if (existingLinks && existingLinks.length > 0) {
        // Use existing link instead of creating a new one - just copy to clipboard
        const existingInviteLink = generateInviteLink(existingLinks[0].id);
        navigator.clipboard.writeText(existingInviteLink);
        toast.success(t('carrierManagement.linkCopiedTitle'));
        return;
      }

      // Create new link only if no active link exists
      const { data: newInvitation, error } = await supabase
        .from('connection_invitations')
        .insert({
          inviter_org_id: currentOrganization.organization_id,
          invited_by: user.id,
          invitation_type: 'link',
          invitation_target: 'generic_link',
          connection_type: 'carrier',
          status: 'active'
        })
        .select('id')
        .single();

      if (error) throw error;

      const inviteLink = generateInviteLink(newInvitation.id);

      // Track successful link generation
      Sentry.addBreadcrumb({
        message: 'Successfully generated carrier invitation link',
        category: 'business_success',
        level: 'info',
        data: { 
          invitationId: newInvitation.id,
          inviterOrgId: currentOrganization.organization_id
        }
      });

      setGeneratedLinks(prev => [...prev, inviteLink]);
      navigator.clipboard.writeText(inviteLink);
      toast.success(t('carrierManagement.linkCopiedTitle'));
    } catch (error) {
      console.error('Error generating link:', error);
      
      // Capture link generation error
      Sentry.captureException(error, {
        tags: {
          operation: 'generate_invitation_link',
          critical: true,
          business_process: 'carrier_invitation'
        },
        extra: {
          inviterOrgId: currentOrganization?.organization_id,
          userId: user?.id
        }
      });
      
      toast.error(t('carrierManagement.linkGenerationError'));
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleClose = () => {
    setGeneratedLinks([]);
    setSelectedCarriers([]);
    setCarrierName("");
    setSearchResults([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('carrierManagement.inviteToNetwork')}</DialogTitle>
          <DialogDescription>
            {t('carrierManagement.inviteDescription')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="space-y-3">
            <Label htmlFor="carrierSearch">{t('carrierManagement.inviteToNetworkLabel')}</Label>
            <div className="relative">
              <Input
                id="carrierSearch"
                placeholder={t('carrierManagement.enterEmailPhone')}
                value={carrierName}
                onChange={(e) => {
                  setCarrierName(e.target.value);
                  searchCarriers(e.target.value);
                }}
              />
              
              {carrierName.length >= 2 && (
                <div className="absolute top-full left-0 right-0 bg-background border border-border rounded-md mt-1 shadow-lg z-50 max-h-60 overflow-y-auto">
                  {/* Show existing organizations */}
                  {searchResults.map((carrier) => (
                    <div
                      key={carrier.id}
                      className={`p-3 border-b flex items-center justify-between ${
                        isAlreadyConnected(carrier.id) 
                          ? 'bg-gray-50 cursor-not-allowed opacity-60' 
                          : 'hover:bg-muted cursor-pointer'
                      }`}
                      onClick={() => {
                        if (!isAlreadyConnected(carrier.id)) {
                          addCarrier(carrier);
                        }
                      }}
                    >
                      <div>
                        <div className="font-medium">{carrier.name}</div>
                        {carrier.username && (
                          <div className="text-sm text-muted-foreground">@{carrier.username}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isAlreadyConnected(carrier.id) && (
                          <Badge variant="default" className="text-xs bg-green-100 text-green-800 border-green-300 pointer-events-none">
                            {t('carrierManagement.connected')}
                          </Badge>
                        )}
                        {!isAlreadyConnected(carrier.id) && hasPendingInvitation(carrier.id) && (
                          <Badge variant="outline" className="text-xs">
                            {t('carrierManagement.pending')}
                          </Badge>
                        )}
                        <div className="text-sm text-muted-foreground capitalize">{carrier.type}</div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Show "Create New" option only for valid email */}
                  {(detectInputType(carrierName) === 'email' && isValidEmail(carrierName)) && (
                    <div
                      className="p-3 hover:bg-muted cursor-pointer border-t flex items-center gap-2 text-primary"
                      onClick={() => {
                        const inputType = detectInputType(carrierName);
                        
                        if (inputType === 'email' && !isValidEmail(carrierName)) {
                          toast.error(t('carrierManagement.invalidEmail'));
                          return;
                        }
                        
                        const isDuplicate = selectedCarriers.some(carrier => 
                          (carrier.isNew && carrier.contact === carrierName) || 
                          carrier.name === carrierName
                        );
                        
                        if (isDuplicate) {
                          toast.error(t('carrierManagement.alreadyAdded'));
                          return;
                        }
                        
                        const newInvitation = {
                          id: `new-${Date.now()}`,
                          name: carrierName,
                          type: inputType === 'email' ? 'Email Invitation' : 'Invitation',
                          isNew: true,
                          contact: carrierName
                        };
                        
                        setSelectedCarriers(prev => [...prev, newInvitation]);
                        setCarrierName("");
                        setSearchResults([]);
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      <div>
                        <div className="font-medium">
                          {detectInputType(carrierName) === 'email' ? t('carrierManagement.inviteByEmail') : 
                           t('carrierManagement.createNewInvitation')}
                        </div>
                        <div className="text-sm text-muted-foreground">{carrierName}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Selected Organizations */}
          {selectedCarriers.length > 0 && (
            <div className="space-y-3">
              <Label>{t('carrierManagement.selectedOrganizations')} ({selectedCarriers.length})</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {selectedCarriers.map((carrier) => (
                  <div key={carrier.id} className="p-2 bg-muted rounded-md flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{carrier.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {carrier.username && `@${carrier.username} • `}
                        <span className="capitalize">{carrier.type}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCarrier(carrier.id)}
                      className="h-6 w-6 p-0"
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generate Links Option */}
          <div className="space-y-3">
            <Label>{t('carrierManagement.shareInvitationLinks')}</Label>
            {generatedLinks.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('carrierManagement.generateShareableLinks')}
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {t('carrierManagement.generatedLinks')}
                </p>
                <div className="bg-muted/50 p-3 rounded-md space-y-2 max-h-32 overflow-y-auto">
                   {generatedLinks.map((link, index) => (
                     <div 
                       key={index} 
                       className="text-xs font-mono break-all bg-background p-2 rounded border flex items-center gap-2 cursor-pointer hover:bg-muted/50 transition-colors"
                       onClick={() => {
                         navigator.clipboard.writeText(link);
                          toast.success(t('carrierManagement.linkCopiedTitle'));
                       }}
                     >
                       <Copy className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                       <span className="flex-1">{link}</span>
                     </div>
                   ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={handleInviteCarriers} 
              className="flex-1" 
              disabled={selectedCarriers.length === 0 || sendingInvitations}
            >
              {sendingInvitations ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              {sendingInvitations 
                ? `${t('carrierManagement.sendingInvitations')} ${selectedCarriers.length} ${selectedCarriers.length !== 1 ? t('carrierManagement.invitations') : t('carrierManagement.invitation')}...` 
                : `${t('carrierManagement.send')} ${selectedCarriers.length} ${selectedCarriers.length !== 1 ? t('carrierManagement.invitations') : t('carrierManagement.invitation')}`
              }
            </Button>
            
            <Button 
              onClick={handleShareGenericLink}
              variant="outline"
              className="flex-1"
              disabled={generatingLink}
              type="button"
            >
              {generatingLink ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('carrierManagement.generatingLink')}
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4 mr-2" />
                  {t('carrierManagement.shareInvitationLink')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};